/**
 * Typed errors for OncoEMR task acknowledgement (`POST .../ack`).
 * @module oncoemrTaskAckErrors
 */

import type { OncoemrTaskAckStaleResponse } from "../../data-model/oncoemrTasksModel";

/**
 * Thrown when `POST .../ack` returns 409 (version mismatch).
 */
export class OncoemrTaskAckStaleError extends Error {
  /** HTTP status for stale optimistic-concurrency ack. */
  readonly status = 409;

  /** Latest `tasks.version` from the server. */
  readonly currentVersion: number;

  /**
   * @param message - Server or client message (e.g. "stale ack — refetch").
   * @param currentVersion - Authoritative version to retry with.
   */
  constructor(message: string, currentVersion: number) {
    super(message);
    this.name = "OncoemrTaskAckStaleError";
    this.currentVersion = currentVersion;
  }
}

/**
 * Resolves the stale-ack payload from a 409 body (top-level or FastAPI `detail` wrapper).
 *
 * @param body - Parsed JSON from the error response.
 * @return `current_version` / `message` fields when present.
 */
const resolveStaleAckPayload = (
  body: unknown,
): Partial<OncoemrTaskAckStaleResponse> => {
  if (typeof body !== "object" || body === null) {
    return {};
  }

  const detail =
    "detail" in body ? (body as { detail?: unknown }).detail : undefined;

  if (typeof detail === "object" && detail !== null && !Array.isArray(detail)) {
    return detail as Partial<OncoemrTaskAckStaleResponse>;
  }

  return body as Partial<OncoemrTaskAckStaleResponse>;
};

/**
 * Parses a 409 ack response body into {@link OncoemrTaskAckStaleError}.
 *
 * @param body - Parsed JSON from the error response (`{ detail: { ... } }` or flat).
 * @returns Stale ack error with `current_version` when present.
 */
export const oncoemrTaskAckStaleErrorFromBody = (
  body: unknown,
): OncoemrTaskAckStaleError => {
  const payload = resolveStaleAckPayload(body);
  const currentVersion =
    typeof payload.current_version === "number" ? payload.current_version : NaN;
  const message =
    typeof payload.message === "string"
      ? payload.message
      : "stale ack — refetch";

  return new OncoemrTaskAckStaleError(message, currentVersion);
};

/**
 * Type guard for {@link OncoemrTaskAckStaleError}.
 *
 * @param error - Caught value from ack.
 * @returns True when the error is a stale-version ack failure.
 */
export const isOncoemrTaskAckStaleError = (
  error: unknown,
): error is OncoemrTaskAckStaleError =>
  error instanceof OncoemrTaskAckStaleError;
